import { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Upload, FileCheck, AlertTriangle, X, CheckCircle2, XCircle, Shield, ShieldCheck, Sparkles, ArrowRight, Calendar, Lock } from 'lucide-react';
import { fbTrack } from '../lib/consent';
import { getUTM } from '../lib/utm';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuditFacturePage = () => {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [lead, setLead] = useState({ prenom: '', email: '' });
  const [captureLoading, setCaptureLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const dropRef = useRef(null);
  const inputRef = useRef(null);
  const resultRef = useRef(null);

  useEffect(() => {
    fbTrack('ViewContent', { content_name: 'Audit Facture 2026', content_category: 'LeadMagnet' });
  }, []);

  const handleFile = (f) => {
    if (!f) return;
    const okType = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'application/pdf'].includes(f.type)
      || /\.(heic|heif|jpe?g|png|webp|pdf)$/i.test(f.name || '');
    if (!okType) return toast.error('Format non supporté — envoyez une photo (JPG, PNG, HEIC) ou un PDF');
    if (f.size > 25 * 1024 * 1024) return toast.error('Fichier trop lourd (max 25 Mo)');
    setFile(f);
    setResult(null);
    setLeadCaptured(false);
    setErrorMsg(null);
    if (f.type.startsWith('image/')) {
      const url = URL.createObjectURL(f);
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }
  };

  // Compression automatique côté client : photos de téléphone (plusieurs Mo)
  // réduites à max 2000px / JPEG 85% avant envoi. L'utilisateur n'a rien à faire.
  const compressImage = (f) =>
    new Promise((resolve) => {
      if (!f.type.startsWith('image/') && !/\.(heic|heif)$/i.test(f.name || '')) return resolve(f);
      const url = URL.createObjectURL(f);
      const img = new Image();
      img.onload = () => {
        try {
          const maxSide = 2000;
          const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(
            (blob) => {
              URL.revokeObjectURL(url);
              if (blob && blob.size > 0) {
                resolve(new File([blob], (f.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' }));
              } else {
                resolve(f);
              }
            },
            'image/jpeg',
            0.85
          );
        } catch {
          URL.revokeObjectURL(url);
          resolve(f);
        }
      };
      // HEIC non décodable par ce navigateur → on envoie l'original, le serveur le gère
      img.onerror = () => { URL.revokeObjectURL(url); resolve(f); };
      img.src = url;
    });

  const onDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dropRef.current?.classList.remove('ring-4', 'ring-emerald-400');
    const f = e.dataTransfer.files?.[0];
    handleFile(f);
  }, []);

  const onDragOver = (e) => {
    e.preventDefault();
    dropRef.current?.classList.add('ring-4', 'ring-emerald-400');
  };
  const onDragLeave = () => dropRef.current?.classList.remove('ring-4', 'ring-emerald-400');

  const runAudit = async () => {
    if (!file) return toast.error('Sélectionnez une facture');
    setLoading(true);
    setErrorMsg(null);
    try {
      const toSend = await compressImage(file);
      const fd = new FormData();
      fd.append('file', toSend);
      const res = await axios.post(`${API}/audit-facture`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setResult(res.data);
      fbTrack('InitiateCheckout', {
        content_name: 'Audit Facture 2026 - Result',
        content_category: 'LeadMagnet',
        value: res.data.score_global,
      });
      // Auto-scroll vers le résultat après un court délai (le temps du rendu)
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    } catch (err) {
      const msg = err.response?.data?.detail
        || (err.code === 'ECONNABORTED' ? "L'analyse a pris un peu trop de temps — réessayez, ça aboutit presque toujours au 2e essai."
        : err.message?.includes('Network') ? "Connexion interrompue. Vérifiez votre réseau et réessayez — votre photo est conservée."
        : "Petit imprévu pendant l'analyse. Cliquez à nouveau sur « Analyser », ça fonctionne généralement du premier coup.");
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.email) return toast.error('Ton email est requis');
    setCaptureLoading(true);
    try {
      const res = await axios.post(`${API}/audit-facture/lead`, {
        email: lead.email,
        prenom: lead.prenom || null,
        score_global: result.score_global,
        verdict: result.verdict,
        utm: getUTM() || null,
      });
      setLeadCaptured(true);
      fbTrack('Lead', {
        content_name: 'Audit Facture 2026',
        content_category: 'LeadMagnet',
        value: 1,
        currency: 'EUR',
      }, res.data.lead_event_id);
      toast.success('Rapport envoyé par email !');
    } catch (err) {
      toast.error("Erreur d'envoi — réessayez");
    } finally {
      setCaptureLoading(false);
    }
  };

  const verdictConfig = {
    conforme: { color: 'emerald', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-300', label: 'CONFORME', icon: CheckCircle2 },
    partiel: { color: 'amber', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300', label: 'PARTIELLEMENT CONFORME', icon: AlertTriangle },
    non_conforme: { color: 'red', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-300', label: 'NON CONFORME', icon: XCircle },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30" data-testid="audit-facture-page">
      {/* Header simple */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-emerald-600" />
            <span className="font-bold text-lg">Capsule Patrimoine</span>
          </Link>
          <Link to="/offre" className="text-sm text-emerald-700 hover:underline">Voir le Pack →</Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-10 sm:py-16">
        {/* Hero */}
        <div className="text-center mb-10 space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
            <Calendar className="h-4 w-4" />
            RÉFORME FACTURATION ÉLECTRONIQUE 2026-2027
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold font-['Manrope'] leading-tight text-slate-900">
            Ta facture est-elle <span className="text-emerald-600">conforme</span> aux mentions obligatoires ?
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Upload une de tes factures — notre IA vérifie <strong>15 mentions légales obligatoires</strong> en 30 secondes.
            Gratuit, sans inscription.
          </p>
          <div className="flex items-center justify-center gap-6 text-xs text-slate-500 pt-2">
            <span className="flex items-center gap-1"><Lock className="h-3 w-3" /> 100% sécurisé</span>
            <span>•</span>
            <span>Analysé par IA Claude 4.6</span>
            <span>•</span>
            <span>Aucun stockage de ta facture</span>
          </div>

          {/* Badge Factur-X — format intégré (XML contrôlé contre le schéma EN 16931) */}
          <div className="pt-4 flex justify-center" data-testid="badge-facturx-audit">
            <div
              className="group inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-gradient-to-r from-emerald-600 to-emerald-500 text-white shadow-lg shadow-emerald-500/30 ring-2 ring-emerald-400/40 cursor-help"
              title="Capsule Patrimoine génère des factures au format Factur-X (profil EN 16931, PDF/A-3), avec XML contrôlé automatiquement par rapport au schéma officiel à chaque génération."
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 backdrop-blur">
                <Shield className="h-5 w-5" strokeWidth={2.5} />
              </div>
              <div className="text-left leading-tight">
                <div className="text-[10px] font-semibold uppercase tracking-wider opacity-90">Format Factur-X intégré</div>
                <div className="text-sm font-bold">Factur-X EN 16931 · PDF/A-3</div>
              </div>
            </div>
          </div>
        </div>

        {/* Error banner visible */}
        {errorMsg && !result && (
          <Card className="shadow-lg border-red-300 bg-red-50 mb-6" data-testid="audit-error-banner">
            <CardContent className="p-4 flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-full flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-red-900">Presque ! Un petit réglage et c&apos;est bon</p>
                <p className="text-sm text-red-700 mt-1">{errorMsg}</p>
                <ul className="text-xs text-red-600 mt-2 space-y-1">
                  <li>📸 Posez la facture à plat, bien éclairée, entièrement visible</li>
                  <li>🔁 Puis cliquez à nouveau sur « Analyser ma facture »</li>
                </ul>
                <button
                  onClick={() => setErrorMsg(null)}
                  className="text-xs text-red-600 underline mt-2 hover:text-red-800"
                >
                  Masquer ce message
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upload zone */}
        {!result && (
          <Card className="shadow-xl border-emerald-100" data-testid="audit-upload-card">
            <CardContent className="p-6 sm:p-10">
              <div
                ref={dropRef}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onClick={() => inputRef.current?.click()}
                className="cursor-pointer border-2 border-dashed border-emerald-300 rounded-2xl p-10 text-center hover:bg-emerald-50/50 transition-all"
                data-testid="audit-dropzone"
              >
                <input
                  type="file"
                  ref={inputRef}
                  className="hidden"
                  accept="image/*,.heic,.heif,application/pdf"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="audit-file-input"
                />
                {!file ? (
                  <>
                    <Upload className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
                    <p className="text-lg font-semibold text-slate-800 mb-1">
                      Prends ta facture en photo ou choisis un fichier
                    </p>
                    <p className="text-sm text-slate-500">Photo (JPG, PNG, HEIC) ou PDF — la photo est optimisée automatiquement</p>
                  </>
                ) : (
                  <div className="space-y-3">
                    <div className="inline-flex items-center gap-3 bg-white border rounded-lg px-4 py-3 shadow-sm">
                      <FileCheck className="h-6 w-6 text-emerald-600" />
                      <div className="text-left">
                        <p className="font-medium text-slate-800">{file.name}</p>
                        <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(0)} Ko</p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setFile(null); setPreviewUrl(null); }}
                        className="p-1 hover:bg-slate-100 rounded"
                        aria-label="Retirer"
                      >
                        <X className="h-4 w-4 text-slate-500" />
                      </button>
                    </div>
                    {previewUrl && (
                      <img src={previewUrl} alt="preview" className="max-h-40 mx-auto rounded shadow" />
                    )}
                  </div>
                )}
              </div>

              <Button
                size="lg"
                className="w-full mt-6 bg-emerald-600 hover:bg-emerald-700 text-white text-base py-6"
                disabled={!file || loading}
                onClick={runAudit}
                data-testid="btn-lancer-audit"
              >
                {loading ? (
                  <>
                    <Sparkles className="h-5 w-5 mr-2 animate-spin" />
                    Analyse en cours (30s)...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5 mr-2" />
                    Analyser ma facture gratuitement
                  </>
                )}
              </Button>

              {/* Trust indicators */}
              <div className="grid grid-cols-3 gap-4 mt-8 pt-6 border-t text-center">
                <div>
                  <div className="text-2xl font-bold text-emerald-600">15</div>
                  <div className="text-xs text-slate-500">mentions vérifiées</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-emerald-600">30s</div>
                  <div className="text-xs text-slate-500">analyse IA</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-emerald-600">0€</div>
                  <div className="text-xs text-slate-500">100% gratuit</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Résultats */}
        {result && (
          <div ref={resultRef} className="space-y-6 scroll-mt-24" data-testid="audit-result">
            {/* Score card */}
            {(() => {
              const cfg = verdictConfig[result.verdict] || verdictConfig.partiel;
              const Icon = cfg.icon;
              return (
                <Card className={`shadow-xl ${cfg.border} border-2`}>
                  <CardContent className={`p-8 ${cfg.bg}`}>
                    <div className="flex flex-col sm:flex-row items-center gap-6">
                      <div className={`p-4 rounded-full bg-white shadow-md ${cfg.text}`}>
                        <Icon className="h-12 w-12" />
                      </div>
                      <div className="flex-1 text-center sm:text-left">
                        <p className={`text-xs font-semibold uppercase tracking-wider ${cfg.text}`}>Verdict</p>
                        <h2 className={`text-2xl sm:text-3xl font-bold ${cfg.text}`} data-testid="audit-verdict-label">
                          Ta facture est {cfg.label}
                        </h2>
                        <p className="text-slate-600 mt-1">
                          Score global : <strong className="text-3xl align-middle" data-testid="audit-score">{result.score_global}</strong>
                          <span className="text-slate-400">/100</span>
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Checklist */}
            <Card className="shadow-lg">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-emerald-600" />
                  Détail des 15 mentions légales
                </h3>
                <div className="space-y-2" data-testid="audit-checks">
                  {result.checks?.map((c) => (
                    <div
                      key={c.id}
                      className={`flex items-start gap-3 p-3 rounded-lg ${
                        c.present ? 'bg-emerald-50' : c.critique ? 'bg-red-50' : 'bg-amber-50'
                      }`}
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {c.present ? (
                          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                        ) : (
                          <XCircle className={`h-5 w-5 ${c.critique ? 'text-red-600' : 'text-amber-600'}`} />
                        )}
                      </div>
                      <div className="flex-1 text-sm">
                        <p className={`font-medium ${c.present ? 'text-slate-800' : c.critique ? 'text-red-900' : 'text-amber-900'}`}>
                          {c.label}
                          {c.critique && !c.present && (
                            <span className="ml-2 inline-block px-2 py-0.5 text-xs bg-red-600 text-white rounded-full">Critique</span>
                          )}
                        </p>
                        {c.commentaire && <p className="text-xs text-slate-500 mt-0.5">{c.commentaire}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Risques 2026 */}
            {result.risques_2026?.length > 0 && (
              <Card className="shadow-lg border-red-200">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-red-700">
                    <AlertTriangle className="h-5 w-5" />
                    Points de vigilance pour la réforme 2026-2027
                  </h3>
                  <ul className="space-y-2">
                    {result.risques_2026.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <span className="text-red-500 mt-0.5">▸</span>
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Lead capture */}
            {!leadCaptured ? (
              <Card className="shadow-xl bg-gradient-to-br from-emerald-600 to-emerald-700 border-0">
                <CardContent className="p-8 text-white">
                  <div className="max-w-lg mx-auto text-center space-y-4">
                    <Sparkles className="h-10 w-10 mx-auto text-white/90" />
                    <h3 className="text-2xl font-bold">Reçois le rapport détaillé + les recommandations par email</h3>
                    <p className="text-emerald-50 text-sm">
                      Ton audit complet + guide de mise en conformité 2026 en PDF, envoyé instantanément.
                    </p>
                    <form onSubmit={submitLead} className="space-y-3 mt-6">
                      <Input
                        placeholder="Ton prénom (facultatif)"
                        value={lead.prenom}
                        onChange={(e) => setLead((l) => ({ ...l, prenom: e.target.value }))}
                        className="bg-white text-slate-900 h-12"
                        data-testid="lead-prenom"
                      />
                      <Input
                        type="email"
                        placeholder="ton@email.com"
                        value={lead.email}
                        onChange={(e) => setLead((l) => ({ ...l, email: e.target.value }))}
                        className="bg-white text-slate-900 h-12"
                        required
                        data-testid="lead-email"
                      />
                      <Button
                        type="submit"
                        size="lg"
                        disabled={captureLoading}
                        className="w-full bg-white text-emerald-700 hover:bg-emerald-50 font-bold h-12"
                        data-testid="btn-envoyer-rapport"
                      >
                        {captureLoading ? 'Envoi...' : (
                          <>Recevoir mon rapport détaillé <ArrowRight className="h-4 w-4 ml-2" /></>
                        )}
                      </Button>
                    </form>
                    <p className="text-xs text-emerald-100/80 mt-3">
                      Aucun spam. Tu peux te désinscrire à tout moment.
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="shadow-lg border-emerald-300 bg-emerald-50">
                <CardContent className="p-8 text-center">
                  <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-3" />
                  <h3 className="text-xl font-bold text-emerald-900">Rapport envoyé à {lead.email} ✅</h3>
                  <p className="text-emerald-700 mt-2">
                    Regarde ta boîte mail dans quelques secondes (et ton dossier spam au cas où).
                  </p>
                  <Link to="/offre">
                    <Button size="lg" className="mt-6 bg-emerald-600 hover:bg-emerald-700 text-white">
                      Découvrir le Pack Capsule Patrimoine <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}

            {/* Restart */}
            <div className="text-center pt-4">
              <button
                onClick={() => { setResult(null); setFile(null); setPreviewUrl(null); setLeadCaptured(false); }}
                className="text-sm text-slate-500 hover:text-slate-700 underline"
              >
                ← Analyser une autre facture
              </button>
            </div>
          </div>
        )}

        {/* Footer social proof */}
        <div className="mt-16 text-center space-y-4">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Ils ont déjà testé leur conformité</p>
          <p className="text-2xl font-bold text-slate-700">1 247 auto-entrepreneurs</p>
          <div className="flex items-center justify-center gap-1 text-amber-500">
            {[1, 2, 3, 4, 5].map((i) => <span key={i}>★</span>)}
            <span className="text-slate-600 text-sm ml-2">4.9/5</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditFacturePage;
